import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Facebook, Linkedin, Instagram } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { siteConfig } from '../data';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  // Handle scroll
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY > 100) {
        setScrolled(true);
      } else if (currentScrollY < 50) {
        setScrolled(false);
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  // Lock body scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const navigation = [
    { name: 'Forside', href: '/' },
    { name: 'Om', href: '/om' },
    { name: '1-1 Forløb', href: '/trivlogi-1-1' },
    { name: 'Trivsel i fællesskabet', href: '/trivsel-i-faellesskabet' },
    { name: 'Skærmen, hjernen og trivslen', href: '/skaermen-hjernen-og-trivslen' },
    { name: 'Referencer', href: '/referencer' },
    { name: 'Kontakt', href: '/kontakt' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <>
      <nav 
        className={`sticky top-0 z-50 transition-[padding,background-color,backdrop-filter,box-shadow] duration-300 ease-in-out will-change-[padding,background-color] ${
          scrolled 
            ? 'bg-emerald-950/95 backdrop-blur-md shadow-xl py-2' 
            : 'bg-emerald-950 py-4 xl:py-6'
        }`}
      >
        <div className="w-full px-4 sm:px-6 lg:px-8 xl:px-10 2xl:px-12">
          {/* Desktop Layout - Stable Single Row */}
          <div className="hidden xl:flex items-center justify-between w-full">
            {/* Logo Section */}
            <div className="flex-shrink-0 flex items-center transition-[height] duration-300 ease-in-out">
              <Link to="/" className="flex items-center group">
                <img 
                  src={siteConfig.logo} 
                  alt={siteConfig.name} 
                  className={`w-auto object-contain transition-[height] duration-300 ease-in-out will-change-[height] ${
                    scrolled ? 'h-10 xl:h-12' : 'h-14 xl:h-20'
                  }`}
                />
              </Link>
            </div>

            {/* Navigation Links */}
            <div className="flex items-center justify-center flex-1 px-2 2xl:px-6">
              <div className="flex items-center space-x-2.5 2xl:space-x-5">
                {navigation.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`text-xs 2xl:text-sm font-medium transition-all duration-300 hover:text-emerald-400 whitespace-nowrap relative group py-1 ${
                      isActive(item.href) ? 'text-emerald-400 font-semibold' : 'text-emerald-50/90'
                    }`}
                  >
                    {item.name}
                    <span className={`absolute -bottom-0.5 left-0 w-0 h-0.5 bg-emerald-400 transition-all duration-300 group-hover:w-full ${
                      isActive(item.href) ? 'w-full' : ''
                    }`} />
                  </Link>
                ))}
              </div>
            </div>

            {/* CTA Section */}
            <div className="flex items-center space-x-4">
              <Link
                to="/kontakt"
                className={`bg-white text-emerald-950 font-bold transition-[padding,font-size,transform,box-shadow] duration-300 shadow-md hover:shadow-emerald-400/20 hover:-translate-y-0.5 active:translate-y-0 whitespace-nowrap inline-block text-center rounded-sm ${
                  scrolled ? 'px-4 py-2 text-sm' : 'px-5 py-2.5 text-sm 2xl:px-6 2xl:py-3 2xl:text-base'
                }`}
              >
                Book samtale
              </Link>
            </div>
          </div>

          {/* Mobile Layout */}
          <div className="xl:hidden flex items-center justify-between">
            <Link to="/" className="flex items-center transition-[height] duration-300">
              <img 
                src={siteConfig.logo} 
                alt={siteConfig.name} 
                className={`w-auto object-contain transition-[height] duration-300 ease-in-out will-change-[height] ${
                  scrolled ? 'h-10' : 'h-12'
                }`}
              />
            </Link>

            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-emerald-50 hover:text-white focus:outline-none p-2 transition-colors"
              aria-label="Toggle menu"
            >
              {isOpen ? <X className="h-7 w-7" /> : <Menu className="h-7 w-7" />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile menu - Smooth Sidebar Overlay */}
      <AnimatePresence>
        {isOpen && (
          <div className="fixed inset-0 overflow-hidden z-50 pointer-events-none xl:hidden">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
              className="absolute inset-0 bg-emerald-950/80 backdrop-blur-sm pointer-events-auto"
            />

            {/* Sidebar */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="absolute right-0 top-0 bottom-0 w-[80%] max-w-sm bg-emerald-900 shadow-2xl flex flex-col pointer-events-auto"
            >
              <div className="flex items-center justify-between p-6 border-b border-white/10">
                <Link to="/" className="flex items-center">
                  <img 
                    src={siteConfig.logo} 
                    alt={siteConfig.name} 
                    className="h-10 w-auto object-contain"
                  />
                </Link>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-2 text-emerald-50 hover:text-white transition-colors"
                  aria-label="Close menu"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto py-6 px-6 space-y-1">
                {navigation.map((item, index) => (
                  <motion.div
                    key={item.name}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1 + index * 0.05 }}
                  >
                    <Link
                      to={item.href}
                      className={`block py-1.5 text-lg font-medium transition-all ${
                        isActive(item.href) 
                          ? 'text-white translate-x-2' 
                          : 'text-emerald-50/70 hover:text-white hover:translate-x-2'
                      }`}
                    >
                      {item.name}
                    </Link>
                  </motion.div>
                ))}
                
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="pt-6 w-full"
                >
                  <Link
                    to="/kontakt"
                    className="block w-full text-center bg-white text-emerald-950 px-6 py-3 text-base font-bold shadow-lg active:scale-95 transition-all"
                  >
                    Book samtale
                  </Link>
                </motion.div>
              </div>

              <div className="p-6 border-t border-white/10 flex flex-col gap-4">
                <div className="flex items-center space-x-4">
                  {siteConfig.social.facebook && (
                    <a 
                      href={siteConfig.social.facebook} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="bg-emerald-800/50 hover:bg-emerald-700 p-2.5 text-emerald-100 hover:text-white transition-all rounded-sm"
                      aria-label="Facebook"
                    >
                      <Facebook className="h-5 w-5" />
                    </a>
                  )}
                  {siteConfig.social.linkedin && (
                    <a 
                      href={siteConfig.social.linkedin} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="bg-emerald-800/50 hover:bg-emerald-700 p-2.5 text-emerald-100 hover:text-white transition-all rounded-sm"
                      aria-label="LinkedIn"
                    >
                      <Linkedin className="h-5 w-5" />
                    </a>
                  )}
                  {siteConfig.social.instagram && (
                    <a 
                      href={siteConfig.social.instagram} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="bg-emerald-800/50 hover:bg-emerald-700 p-2.5 text-emerald-100 hover:text-white transition-all rounded-sm"
                      aria-label="Instagram"
                    >
                      <Instagram className="h-5 w-5" />
                    </a>
                  )}
                </div>
                <p className="text-emerald-50/50 text-sm italic">
                  "Dit Liv - Dit Ansvar"
                </p>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
